import { fireEvent, screen } from '@testing-library/preact';
import { VNode } from 'preact';
import { ListItemRendererContext, SelectionMode } from '../../UNSAFE_Collection';
import { ActionDetail } from '../../UNSAFE_BaseButton';
import { Avatar } from '../../UNSAFE_Avatar';
import { Button } from '../../UNSAFE_Button';
import { Text } from '../../UNSAFE_Text';
import { View } from '../../UNSAFE_View';
import { Flex } from '../../UNSAFE_Flex';
import { Center } from '../../UNSAFE_Center';
import { DragHandle } from '../../UNSAFE_DragHandle';
import { RootEnvironmentProvider } from '../../UNSAFE_Environment';
import { Employee } from '../__dev__/BaseCardViewData';

export type GenericCardGridProps<K extends string | number> = {
  data: Employee[];
  delay?: number;
  isReorderEnabled?: boolean;
  initialCurrentKey?: K;
  fetchSize?: number;
};

export type ActionCardGridProps<K extends string | number> = GenericCardGridProps<K> & {
  handleAction?: (details: ActionDetail) => void;
  showButton?: boolean; // for testing invalid focusBehavior
};

export type SelectionCardGridProps<K extends string | number> = GenericCardGridProps<K> & {
  selectionMode?: SelectionMode;
  selectedKeys?: Set<Employee['id']>;
};

export type TestComponentsProps<K extends string | number> = {
  GenericCardGrid: (props: GenericCardGridProps<K>) => VNode;
  ActionCardGrid: (props: ActionCardGridProps<K>) => VNode;
  SelectionCardGrid: (props: SelectionCardGridProps<K>) => VNode;
} & Record<string, (props: any) => VNode>;

export const CardContent = ({
  context,
  showButton,
  isReorderEnabled
}: {
  context: ListItemRendererContext<Employee['id'], Employee>;
  showButton?: boolean;
  isReorderEnabled?: boolean;
}) => {
  const dragIcon = isReorderEnabled && (
    <RootEnvironmentProvider environment={{ mode: 'test' }}>
      <Center>
        <DragHandle testId={`drag-icon-${context.metadata.key}`} />
      </Center>
    </RootEnvironmentProvider>
  );
  const buttons = showButton && (
    <View paddingBlockStart={'1x'}>
      <Flex direction="row" wrap="wrap" gap={'1x'} width={'100%'}>
        <Button variant="outlined" size="md" label="A" />
        <Button variant="outlined" size="md" label="B" />
      </Flex>
    </View>
  );

  return (
    <View padding="6px">
      {dragIcon}
      <View padding="10px" paddingBlockEnd={0}>
        <Avatar initials={context.data.initials} size="xl" />
        <View paddingBlockStart="2x">
          <Text variant="primary" size="md" lineClamp={1}>
            {context.data.name}
          </Text>
          <Text variant="secondary" size="md" lineClamp={1}>
            {context.data.title}
          </Text>
        </View>
      </View>
      <View padding="10px" paddingBlockStart={0}>
        {buttons}
        {context.selector?.()}
      </View>
    </View>
  );
};

export const populateData = (count: number, isNumberKey?: boolean) =>
  [...Array(count)].map(
    (_, i) =>
      ({
        id: isNumberKey ? i : `${i}`,
        name: `Employee ${i}`,
        title: `Employee title ${i}`,
        initials: `E${i}`
      }) as Employee
  );

export const getRoot = async () => {
  return screen.findByRole('grid');
};

export const getItems = async (role = 'row') => {
  return screen.findAllByRole(role);
};

export const getContents = async (role = 'gridcell') => {
  return screen.findAllByRole(role);
};

export const mouseItemSelection = (element: HTMLElement, options?: Record<string, unknown>) => {
  fireEvent.click(element, options);
};

export const fireDragAndDropEvent = (dragElement: HTMLElement, dropElement: HTMLElement) => {
  fireEvent.dragStart(dragElement);
  fireEvent.dragEnter(dropElement);
  fireEvent.drop(dropElement);
  fireEvent.dragEnd(dragElement);
};

export const keyboardReorder = (
  element: HTMLElement,
  keyCode: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown'
) => {
  fireEvent.keyDown(element, {
    key: keyCode,
    code: keyCode,
    shiftKey: true,
    metaKey: true
  });
};
